import { formatDate, toDate } from '@art-suite/art-core-ts';

import { presentUser } from '@kaiizen/kai-presenters';
import { ApiOrgProperty, ApiPropertyProject } from '@kaiizen/kai-pro-api';

import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import {
  useAuth,
  useOrgMarket,
  useOrgMarkets,
  useProjectTypes,
  useScanTemplates,
  useUsers,
  useUsersByOrgMarketId,
} from '../../State';
import { useNavigateToMarkets, useNavigateToProject, useNavigateToProperty, useNavigateToScanTemplates } from '../../State/kaiNavigation';
import { getInitialProjectManagerIds } from './projectPanelLib';

/** What a new project (a "job") is created from - the create form's fields. */
export type NewPropertyProjectValues = {
  name: string;
  budget: number | '';
  description?: string;
  projectType: string;
  projectDueDate?: string | Date;
  scanDueDate?: string | Date;
  gateCode: string;
  doorCode: string;
  scanTemplateId: string;
  assignedUserId: string;
  managerIds: string[];
};

/** Something the org has to set up before a project can be created here, and where to fix it. */
export type NewPropertyProjectBlocker = {
  message: string;
  actions: { label: string; onClick: () => void }[];
};

type PropertyProjectMutators = { create: (data: any) => Promise<ApiPropertyProject> };

/**
 * Everything both create-project forms need - the legacy flyout (`ProjectPanel`) and the new
 * experience's New job modal - so they can't drift apart: what blocks creating one here, the
 * choices and defaults, validation, and the create itself (which then opens the new project).
 *
 * `status` is `loading` until the org's markets, the market's users and the org's users have loaded, `blocked`
 * when something must be set up first (`blocker` says what), else `ready`.
 */
export const useCreatePropertyProject = (property: ApiOrgProperty, projectMutators: PropertyProjectMutators) => {
  const { user: currentUser } = useAuth();
  const orgMarketId = property.orgMarketId ?? undefined;
  const { orgMarket } = useOrgMarket(orgMarketId);
  const { projectTypeOptions } = useProjectTypes();
  const { scanTemplates } = useScanTemplates();
  const { markets } = useOrgMarkets();
  const { users: usersByMarket } = useUsersByOrgMarketId(orgMarketId);
  const navigateToProject = useNavigateToProject();
  const navigateToScanTemplates = useNavigateToScanTemplates();
  const navigateToMarkets = useNavigateToMarkets();
  const navigateToProperty = useNavigateToProperty();

  const activeScanTemplates = scanTemplates.filter(template => !template.archivedAt);
  const templateOptions = activeScanTemplates.map(template => ({ value: template.id, label: template.name }));
  /** The market's default template, when it names one that still exists. */
  const defaultScanTemplate = orgMarket?.defaultTemplateId
    ? activeScanTemplates.find(({ id }) => id === orgMarket.defaultTemplateId)
    : undefined;

  // Field users must be filtered to the property's market. Never fall back to the full org user
  // list: doing so made every user flash in the dropdown (and stay selectable) while
  // `usersByMarket` was still loading.
  const assignableUsers = usersByMarket ?? [];
  const userOptions = assignableUsers.map(user => ({ value: user.id, label: presentUser(user) }));
  // Any org member can manage (the server's rule), not just people linked to this market.
  const { users: orgUsers } = useUsers();
  const managerOptions = (orgUsers ?? []).map(user => ({ value: user.id, label: presentUser(user) }));
  const getManagerLabel = (userId: string) => {
    const user = orgUsers?.find(({ id }) => id === userId) ?? (currentUser?.id === userId ? currentUser : undefined);
    return user ? presentUser(user) : 'Unknown user';
  };

  const fixProperty = { label: "Set Property's Market", onClick: () => navigateToProperty({ propertyId: property.id, edit: true }) };
  // Checked in this order, once the org's markets have loaded - the order the legacy panel used.
  const blocker: NewPropertyProjectBlocker | undefined = !markets
    ? undefined
    : templateOptions.length === 0
    ? {
      message: 'No scan templates available. Create a scan template first.',
      actions: [{ label: 'Add a Template', onClick: () => navigateToScanTemplates() }],
    }
    : markets.length === 0
    ? { message: 'No market available. Create a market first.', actions: [{ label: 'Add a Market', onClick: () => navigateToMarkets() }] }
    : !orgMarket
    ? { message: 'Property must be associated with a market.', actions: [fixProperty] }
    : usersByMarket && usersByMarket.length === 0
    ? {
      message: `No users available in property's market: ${orgMarket.name}.`,
      actions: [
        { label: `Add Users to ${orgMarket.name} market`, onClick: () => navigateToMarkets({ id: orgMarket.id }) },
        { label: "Change Property's Market", onClick: fixProperty.onClick },
      ],
    }
    : undefined;
  const status: 'loading' | 'blocked' | 'ready' = !markets ? 'loading' : blocker ? 'blocked' : !usersByMarket || !orgUsers ? 'loading' : 'ready';

  const initialValues: NewPropertyProjectValues = {
    name: '',
    budget: '',
    description: '',
    projectType: '',
    projectDueDate: formatDate(new Date(), 'yyyy-MM-dd'),
    scanDueDate: formatDate(new Date(), 'yyyy-MM-dd'),
    gateCode: '',
    doorCode: '',
    scanTemplateId: defaultScanTemplate?.id ?? '',
    assignedUserId: '',
    // Every project has a manager: the market's default managers who are still org members, else the signed-in user.
    managerIds: getInitialProjectManagerIds(orgMarket?.defaultManagerIds, (orgUsers ?? []).map(user => user.id), currentUser?.id),
  };

  const validate = (values: NewPropertyProjectValues) => {
    const errors: Partial<Record<keyof NewPropertyProjectValues, string>> = {};
    if (values.budget !== '' && isNaN(parseFloat(String(values.budget)))) errors.budget = 'Budget must be a valid number';
    if (!values.projectType) errors.projectType = 'Project type is required';
    if (!values.scanTemplateId) errors.scanTemplateId = 'Project template is required';
    if (!values.assignedUserId) errors.assignedUserId = 'Field user is required';
    if (!values.managerIds?.length) errors.managerIds = 'At least one project manager is required';
    return errors;
  };

  /** Creates the project and its first scan, then opens the project. */
  const createProject = async (values: NewPropertyProjectValues) => {
    const budget = values.budget === '' ? '' : parseFloat(String(values.budget));
    const project = await projectMutators.create({
      name: values.name,
      description: values.description,
      orgId: property.orgId,
      orgPropertyId: property.id,
      status: 'active',
      managerIds: values.managerIds,
      customerMetadata: {
        budget,
        projectType: values.projectType,
        dueDate: values.projectDueDate ? toDate(values.projectDueDate).toISOString() : undefined,
      },
      scan: {
        name: values.name,
        scanTemplateId: values.scanTemplateId,
        assignedUserId: values.assignedUserId,
        customerMetadata: {
          dueDate: values.scanDueDate ? toDate(values.scanDueDate).toISOString() : undefined,
          gateCode: values.gateCode,
          doorCode: values.doorCode,
        },
      },
    });

    trackEvent(ANALYTICS_EVENTS.projectCreated, {
      projectId: project.id,
      propertyId: property.id,
      projectType: values.projectType,
      hasBudget: !!budget,
      scanTemplateId: values.scanTemplateId,
      assignedUserId: values.assignedUserId,
    });
    trackEvent(ANALYTICS_EVENTS.scanCreated, {
      scanId: project.firstScanId,
      projectId: project.id,
      scanTemplateId: values.scanTemplateId,
      scanType: 'initial',
    });

    navigateToProject({ projectId: project.id });
    return project;
  };

  return {
    status,
    blocker,
    orgMarket,
    projectTypeOptions,
    templateOptions,
    defaultScanTemplate,
    userOptions,
    managerOptions,
    getManagerLabel,
    initialValues,
    validate,
    createProject,
  };
};
